import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  AlertCamera,
  AlertCameraGroup,
  AlertCameraServer,
  RealtimeAlertCamera,
  RealtimeAlertCameraServer,
} from 'features/cameras/cameras.types';
import {
  transformAlertCameraServerData,
  transformRealtimeAlertCameraServerData,
} from 'features/cameras/cameras.utils';
import { groupCamerasByClusterId } from './useAlertCameras.utils';
import { API } from '../../../../api';

const LOCAL_CACHE_TIME_MS = 1000 * 60 * 60 * 24; // 24 hours

const REALTIME_POLL_FREQUENCY_MS = 1000 * 15; // 15 sec

export type UseAlertCamerasProps = {
  enabled: boolean;
  withRealtimeUpdates?: boolean;
};

export type UseAlertCameras = {
  alertCameras?: AlertCamera[];
  alertCameraGroups?: AlertCameraGroup[];
  isLoading: boolean;
  error: Error | null;
};

export type UseAlertCameraProps = {
  id?: string;
  refetchInterval?: number;
};

export type UseAlertCamera = {
  alertCamera?: AlertCamera;
  isLoading: boolean;
  error: Error | null;
};

type AlertCamerasListResponse = {
  data: AlertCameraServer[];
};

type RealtimeAlertCamerasResponse = {
  data: RealtimeAlertCameraServer[];
};

type AlertCameraInstanceResponse = {
  data: AlertCameraServer;
};

const adaptAlertCameraListData = (
  cameras: AlertCameraServer[],
): AlertCamera[] => cameras.map((c) => transformAlertCameraServerData(c));

const adaptRealtimeAlertCameraListData = (
  cameras: RealtimeAlertCameraServer[],
): RealtimeAlertCamera[] =>
  cameras.map((c) => transformRealtimeAlertCameraServerData(c));

export const fetchAlertCameras = async (): Promise<AlertCameraServer[]> => {
  const res = await API.get<null, AlertCamerasListResponse>(`cameras_gis/`);
  return res.data;
};

const fetchRealtimeAlertCameras = async (): Promise<
  RealtimeAlertCameraServer[]
> => {
  const res = await API.get<null, RealtimeAlertCamerasResponse>(
    `cameras_gis/realtime`,
  );
  return res.data;
};

export const fetchAlertCameraDetails = async (
  compositeId: string,
): Promise<AlertCameraServer> => {
  const res = await API.get<null, AlertCameraInstanceResponse>(
    `cameras_gis/${compositeId}`,
  );
  return res.data;
};

export type UseRealtimeAlertCamerasProps = {
  enabled: boolean;
};

export type UseRealtimeAlertCameras = {
  realtimeAlertCameras?: RealtimeAlertCamera[];
  error: Error | null;
};

// Pose, offline state and last movement of PTZ cameras with an azimuth
export const useRealtimeAlertCameras = (
  props: UseRealtimeAlertCamerasProps,
): UseRealtimeAlertCameras => {
  const { data: realtimeAlertCameras, error } = useQuery({
    queryKey: ['realtimeAlertCameras'],
    queryFn: fetchRealtimeAlertCameras,
    select: adaptRealtimeAlertCameraListData,
    refetchInterval: REALTIME_POLL_FREQUENCY_MS,
    enabled: props.enabled,
  });

  return { realtimeAlertCameras, error };
};

export const useAlertCameras = (
  props: UseAlertCamerasProps,
): UseAlertCameras => {
  const { enabled, withRealtimeUpdates = false } = props;

  const {
    data: allCamerasData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['alertCameras'],
    queryFn: fetchAlertCameras,
    select: adaptAlertCameraListData,
    staleTime: LOCAL_CACHE_TIME_MS,
    gcTime: LOCAL_CACHE_TIME_MS,
    enabled,
  });

  const { realtimeAlertCameras, error: realtimeError } =
    useRealtimeAlertCameras({ enabled: enabled && withRealtimeUpdates });

  const allCamerasDataWithRealtime = useMemo(() => {
    const allCamerasDataSafe = allCamerasData || [];
    const realtimeAlertCamerasSafe =
      (withRealtimeUpdates && realtimeAlertCameras) || [];
    const realtimeAlertCamerasMap = new Map<string, RealtimeAlertCamera>();
    realtimeAlertCamerasSafe.forEach((c) => {
      realtimeAlertCamerasMap.set(c.compositeId, c);
    });

    return allCamerasDataSafe.map((c) => {
      const realtimeCamera = realtimeAlertCamerasMap.get(c.compositeId);
      return realtimeCamera ? { ...c, ...realtimeCamera } : c;
    });
  }, [allCamerasData, withRealtimeUpdates, realtimeAlertCameras]);

  const cameraGroups = useMemo(() => {
    return groupCamerasByClusterId(allCamerasDataWithRealtime);
  }, [allCamerasDataWithRealtime]);

  return {
    alertCameras: allCamerasDataWithRealtime,
    alertCameraGroups: cameraGroups,
    isLoading,
    error: error || realtimeError,
  };
};

export const useAlertCamera = (props: UseAlertCameraProps): UseAlertCamera => {
  const { id, refetchInterval } = props;

  const {
    data: alertCamera,
    isLoading,
    error,
  } = useQuery({
    queryKey: [`alertCameraInstance-${id}`],
    queryFn: () => fetchAlertCameraDetails(id!),
    enabled: Boolean(id),
    refetchInterval,
  });

  const transformedCamera = alertCamera
    ? transformAlertCameraServerData(alertCamera)
    : undefined;

  return { alertCamera: transformedCamera, isLoading, error };
};
